import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface InternalAuthState {
  isInternalMode: boolean;
  clickCount: number;
  lastClickTime: number;
  
  handleLogoClick: () => void;
  activateInternalMode: () => void;
  resetInternalMode: () => void;
  resetClickCount: () => void;
}

const CLICK_TIMEOUT = 5000; // 5 seconds to complete sequence
const REQUIRED_CLICKS = 5;

export const useInternalAuthStore = create<InternalAuthState>()(
  persist(
    (set, get) => ({
      isInternalMode: false,
      clickCount: 0,
      lastClickTime: 0,

      handleLogoClick: () => {
        const state = get();
        const now = Date.now();
        
        // Reset if too much time has passed
        if (now - state.lastClickTime > CLICK_TIMEOUT) {
          set({ clickCount: 1, lastClickTime: now });
          return;
        }
        
        const newClickCount = state.clickCount + 1;
        
        if (newClickCount >= REQUIRED_CLICKS) {
          // Ready for Enter key activation
          set({ clickCount: newClickCount, lastClickTime: now });
          
          // Set up Enter key listener
          const handleKeyPress = (event: KeyboardEvent) => {
            if (event.key === 'Enter') {
              get().activateInternalMode();
              document.removeEventListener('keydown', handleKeyPress);
            }
          };
          
          document.addEventListener('keydown', handleKeyPress);
          
          // Remove listener after timeout
          setTimeout(() => {
            document.removeEventListener('keydown', handleKeyPress);
            get().resetClickCount();
          }, CLICK_TIMEOUT);
        } else {
          set({ clickCount: newClickCount, lastClickTime: now });
        }
      },

      activateInternalMode: () => {
        set({ 
          isInternalMode: true, 
          clickCount: 0, 
          lastClickTime: 0 
        });
        
        // Clear any cached authentication state when switching modes
        if (typeof localStorage !== 'undefined') {
          Object.keys(localStorage).forEach(key => {
            if (key.startsWith('@@auth0spajs@@')) {
              localStorage.removeItem(key);
            }
          });
        }
        
        console.log('Internal authentication mode activated - cleared auth cache');
        // Force page reload to reinitialize Auth0 with new client ID
        window.location.reload();
      },

      resetInternalMode: () => {
        set({ 
          isInternalMode: false, 
          clickCount: 0, 
          lastClickTime: 0 
        });
        
        // Clear any cached authentication state when switching modes
        if (typeof localStorage !== 'undefined') {
          Object.keys(localStorage).forEach(key => {
            if (key.startsWith('@@auth0spajs@@')) {
              localStorage.removeItem(key);
            }
          });
        }
        
        console.log('Internal authentication mode deactivated - cleared auth cache');
        window.location.reload();
      },

      resetClickCount: () => {
        set({ clickCount: 0, lastClickTime: 0 });
      }
    }),
    {
      name: 'internal-auth-storage',
      partialize: (state) => ({ isInternalMode: state.isInternalMode })
    }
  )
);
